import * as React from 'react';

import type { NotificationItemDto } from '@/api/notifications-client';
import {
  useMarkNotificationsRead,
  useNotificationList,
} from '@/api/use-notifications';
import { NotificationItemButton } from '@/components/notification-item-button';
import { isUnread } from '@/lib/notification-presentation';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

// =============================================================================
// notification-panel.tsx — Volet consolidé des notifications (R15-COM-06, shadcn Sheet, DBU D-1/D-2).
// =============================================================================
// Liste chronologique (tri récence stricte SERVEUR) du volet consolidé, paginée en keyset (infinite).
// EXCLUT le chat (G-4 : l'endpoint list l'exclut déjà côté serveur ; le front ne le réintègre pas).
//
// DBU D-1 : PLUS de bouton « Tout marquer comme lu » (retiré V1). DBU D-2 : marquage lu AUTOMATIQUE à
// l'AFFICHAGE (clause système, DBU l.3252) — à l'ouverture, les ids AFFICHÉS non-lus sont marqués lus en
// UN LOT (idempotent : jamais deux fois le même id ; aucun POST si rien de non-lu). Le clic item ne fait
// PLUS que naviguer (onItemActivate). A11y : Sheet role="dialog" + aria-label ; liste sémantique.

export interface NotificationPanelProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Activation d'un item (clic/Entrée) : NAVIGUE seulement (résolution via payloadContext, DBU D-2). */
  readonly onItemActivate: (item: NotificationItemDto) => void;
}

export function NotificationPanel({
  open,
  onClose,
  onItemActivate,
}: NotificationPanelProps): React.JSX.Element {
  const list = useNotificationList();
  const markRead = useMarkNotificationsRead();
  const { mutate: markReadMutate } = markRead;

  // Aplatit les pages keyset en une liste unique (déjà triée récence stricte côté serveur).
  const items: readonly NotificationItemDto[] = React.useMemo(
    () => (list.data?.pages ?? []).flatMap((page) => page.items),
    [list.data],
  );

  // Auto-read à l'AFFICHAGE (DBU D-2/l.3252) : lot des ids AFFICHÉS non-lus, marqués lus UNE fois. Un `ref`
  // mémorise les ids déjà envoyés pour rester idempotent (pas de re-POST au refetch post-invalidation, ni à
  // la pagination). Réinitialisé à la fermeture du volet (réévaluation propre à la prochaine ouverture).
  const sentIdsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    if (!open) {
      sentIdsRef.current = new Set();
      return;
    }
    const unreadIds = items
      .filter((item) => isUnread(item))
      .map((item) => item.notificationId)
      .filter((id) => !sentIdsRef.current.has(id));
    if (unreadIds.length === 0) {
      return;
    }
    for (const id of unreadIds) {
      sentIdsRef.current.add(id);
    }
    markReadMutate(unreadIds);
  }, [open, items, markReadMutate]);

  return (
    <Sheet open={open} onClose={onClose} label="Centre de notifications">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">
            Notifications
          </h2>
        </header>

        {list.isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Chargement des notifications…
          </p>
        ) : null}

        {list.isError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-foreground"
          >
            Impossible de charger les notifications : {list.error.message}
          </p>
        ) : null}

        {list.isSuccess && items.length === 0 ? (
          <p role="status" className="text-sm text-muted-foreground">
            Aucune notification pour le moment.
          </p>
        ) : null}

        {items.length > 0 ? (
          <ul className="flex flex-col gap-1">
            {items.map((item) => (
              <li key={item.notificationId}>
                <NotificationItemButton item={item} onActivate={onItemActivate} />
              </li>
            ))}
          </ul>
        ) : null}

        {list.hasNextPage ? (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void list.fetchNextPage()}
              disabled={list.isFetchingNextPage}
            >
              {list.isFetchingNextPage ? 'Chargement…' : 'Charger plus'}
            </Button>
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}
